import React, { useState, useEffect } from "react";
import { getAssignments, submitAssignment } from "../services/assignments";
import { getMyPurchases } from "../services/purchase";
import api from "../services/api";
import toast, { Toaster } from "react-hot-toast";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";

import {
   Calendar, FileText, Code, CheckCircle, Clock, AlertCircle, Star,
   ArrowLeft, CloudUpload, Play, RefreshCcw, ChevronDown, TerminalSquare, Timer
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const DEFAULT_LANGS = [
   { id: "javascript", label: "JavaScript", ext: "js" },
   { id: "python", label: "Python 3", ext: "py" },
   { id: "java", label: "Java 15", ext: "java" },
   { id: "cpp", label: "C++", ext: "cpp" },
];

export default function StudentAssignments() {
   const [courses, setCourses] = useState([]);
   const [selectedCourse, setSelectedCourse] = useState(null);
   const [assignments, setAssignments] = useState([]);
   const [loading, setLoading] = useState(true);
   const [role, setRole] = useState("student");

   // Workspace State
   const [activeTask, setActiveTask] = useState(null);
   const [uploading, setUploading] = useState(false);

   // Coding Playground state
   const [code, setCode] = useState("");
   const [lang, setLang] = useState("python");
   const [isRunning, setIsRunning] = useState(false);
   const [runResults, setRunResults] = useState(null);

   useEffect(() => {
      const token = getToken();
      if (token) {
         try {
            setRole(jwtDecode(token).role || "student");
         } catch { }
      }

      getMyPurchases().then(res => {
         setCourses(res.data);
         if (res.data.length > 0) setSelectedCourse(res.data[0]);
         setLoading(false);
      });
   }, []);


   useEffect(() => {
      if (selectedCourse) {
         getAssignments(selectedCourse.id).then(res => setAssignments(res.data));
      }
   }, [selectedCourse]);

   // Setup code window when activeTask loads or switches
   useEffect(() => {
      if (activeTask && activeTask.type === "coding") {
         // If previously submitted, restore, else load default/nothing
         setCode(activeTask.submission?.submitted_code || "# Enter solution here...");
         setLang(activeTask.submission?.submitted_language || "python");
         setRunResults(null);
      }
   }, [activeTask]);

   const handleFileUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);

      try {
         const formData = new FormData();
         formData.append("file", file);
         const res = await api.post("/upload/file", formData);
         const file_url = res.data.url;

         // 3. Register Submission
         await submitAssignment(activeTask.id, { file_url: file_url });

         toast.success("Homework uploaded perfectly!");
         setActiveTask(null);
         // Refresh list
         const ref = await getAssignments(selectedCourse.id);
         setAssignments(ref.data);
      } catch (err) {
         toast.error("Upload failure.");
      } finally {
         setUploading(false);
      }
   };

   const handleCodeRun = async () => {
      setIsRunning(true);
      setRunResults(null);
      try {
         // Attempt code run just like Arena
         const res = await api.post("/coding/execute", {
            language: lang,
            source_code: code,
            stdin: ""
         });
         const run = res.data?.run;
         setRunResults({
            output: run.output || run.stdout,
            error: run.stderr,
            runtime: 45
         });
         toast("Code executed locally", { icon: "⚙️" });
      } catch (err) {
         toast.error("Execution pipeline failed");
      } finally {
         setIsRunning(false);
      }
   };

   const submitCodingTask = async () => {
      try {
         await submitAssignment(activeTask.id, {
            submitted_code: code,
            submitted_language: lang
         });
         toast.success("Solution sent to Instructor!", { icon: "🚀" });
         setActiveTask(null);
         const ref = await getAssignments(selectedCourse.id);
         setAssignments(ref.data);
      } catch (err) {
         toast.error("Save failed.");
      }
   };

   return (
      <div className="flex min-h-screen bg-[#f8fafc] dark:bg-[#080a10]">
                  <main className="flex-1 p-6 md:p-10 font-[ min-w-0'Inter',sans-serif] dark:text-slate-200 pb-32 lg:ml-64">
            <Toaster />

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
               <div>
                  <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">Homework & Tasks</h1>
                  <p className="text-slate-500 mt-1 font-medium">Submitting your projects elevates your profile rank and unlocks stars.</p>
               </div>

               <div className="flex gap-3 w-full md:w-auto">
                  <select
                     className="flex-1 bg-white dark:bg-slate-900 border dark:border-slate-800 px-4 py-3 rounded-2xl font-bold shadow-sm text-sm outline-none focus:ring-2 ring-indigo-500 transition-all"
                     onChange={(e) => setSelectedCourse(courses.find(c => c.id === parseInt(e.target.value)))}
                     value={selectedCourse?.id || ""}
                  >
                     {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
               </div>
            </div>

            {loading ? (
               <div className="py-20 flex justify-center"><div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" /></div>
            ) : assignments.length === 0 ? (
               <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] p-20 text-center flex flex-col items-center">
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-500 rounded-full mb-4">
                     <CheckCircle size={40} className="opacity-50" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white">You are completely caught up!</h3>
                  <p className="text-slate-500 text-sm mt-2 max-w-xs">No pending assignments found for the selected course module.</p>
               </div>
            ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {assignments.map(task => {
                     const isSubmitted = !!task.submission;
                     const isGraded = task.submission?.status === "graded";
                     const dateObj = new Date(task.deadline);
                     const isOverdue = dateObj < new Date() && !isSubmitted;

                     return (
                        <motion.div
                           key={task.id}
                           whileHover={{ y: -5 }}
                           className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-[2.5rem] p-8 flex flex-col shadow-sm hover:shadow-2xl hover:shadow-indigo-500/5 transition-all group"
                        >
                           <div className="flex justify-between items-start mb-6">
                              <div className={`p-3 rounded-2xl ${task.type === "coding" ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'}`}>
                                 {task.type === "coding" ? <Code size={24} /> : <FileText size={24} />}
                              </div>

                              {isGraded ? (
                                 <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full font-black text-xs">
                                    <Star fill="currentColor" size={12} /> {task.submission.score}/{task.max_points}
                                 </div>
                              ) : isSubmitted ? (
                                 <div className="bg-slate-100 dark:bg-slate-800 text-slate-500 px-3 py-1 rounded-full font-black text-[10px] uppercase">Submitted</div>
                              ) : isOverdue ? (
                                 <div className="bg-rose-50 dark:bg-rose-950/30 text-rose-600 px-3 py-1 rounded-full font-black text-[10px] uppercase flex items-center gap-1"><AlertCircle size={10} /> Overdue</div>
                              ) : (
                                 <div className="bg-amber-50 dark:bg-amber-950/30 text-amber-600 px-3 py-1 rounded-full font-black text-[10px] uppercase">Active</div>
                              )}
                           </div>

                           <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight group-hover:text-indigo-600 transition-colors">{task.title}</h3>
                           <p className="text-sm text-slate-500 mt-2 line-clamp-2 flex-1">{task.description}</p>

                           <div className="mt-6 pt-6 border-t dark:border-slate-800 flex items-center justify-between">
                              <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                                 <Clock size={14} /> {dateObj.toLocaleDateString()}
                              </div>

                              <button
                                 onClick={() => setActiveTask(task)}
                                 className={`px-5 py-2 rounded-xl font-black text-xs transition-all ${isSubmitted && !isGraded ? 'bg-slate-100 dark:bg-slate-800 text-slate-600' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20'}`}
                              >
                                 {isGraded ? "View Results" : isSubmitted ? "Update Submit" : "Open Task"}
                              </button>
                           </div>
                        </motion.div>
                     );
                  })}
               </div>
            )}

            {/* FULL SCREEN WORKSPACE MODAL */}
            <AnimatePresence>
               {activeTask && (
                  <div className="fixed inset-0 z-[60] bg-slate-950 flex flex-col text-slate-200 overflow-hidden">
                     {/* Header Workspace */}
                     <header className="h-16 shrink-0 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6 shadow-md">
                        <div className="flex items-center gap-4">
                           <button onClick={() => setActiveTask(null)} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 transition-all"><ArrowLeft size={20} /></button>
                           <h2 className="font-black text-lg text-white truncate max-w-sm md:max-w-lg">{activeTask.title}</h2>
                        </div>

                        <div className="flex items-center gap-4">
                           {activeTask.type === "coding" && (
                              <button
                                 onClick={submitCodingTask}
                                 className="bg-indigo-600 text-white px-6 py-2 rounded-xl font-black text-sm hover:bg-indigo-700 shadow-lg transition-all"
                              >
                                 Deliver Solution
                              </button>
                           )}
                           {activeTask.submission?.status === "graded" && (
                              <div className="flex items-center gap-2 bg-emerald-500 text-white font-black px-4 py-1.5 rounded-full text-xs">
                                 <Star fill="currentColor" size={14} /> Graded
                              </div>
                           )}
                        </div>
                     </header>

                     {/* Body split content */}
                     <main className="flex-1 flex flex-col md:flex-row overflow-hidden min-w-0">
                        {/* Sidebar: instructions / feedback */}
                        <div className="w-full md:w-[350px] border-r border-slate-800 bg-slate-900/50 p-8 overflow-y-auto flex flex-col custom-scrollbar shrink-0">
                           <div className="flex-1">
                              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400 mb-3">Assignment Detail</h3>
                              <p className="text-slate-300 leading-relaxed whitespace-pre-line font-medium">{activeTask.description}</p>

                              <div className="mt-6 p-4 bg-slate-900 border border-slate-800 rounded-2xl text-xs space-y-2">
                                 <div className="flex justify-between"><span className="text-slate-500">Format</span><span className="font-bold text-slate-200 capitalize">{activeTask.type}</span></div>
                                 <div className="flex justify-between"><span className="text-slate-500">Weight</span><span className="font-bold text-slate-200">{activeTask.max_points} Points</span></div>
                              </div>
                           </div>

                           {activeTask.submission?.feedback && (
                              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 p-5 bg-indigo-950/30 border border-indigo-900/50 rounded-2xl">
                                 <div className="flex items-center gap-2 text-indigo-400 font-black text-xs uppercase tracking-widest mb-2">
                                    <Star size={14} fill="currentColor" /> Instructor Feedback
                                 </div>
                                 <p className="italic text-slate-200 font-medium leading-relaxed text-sm">"{activeTask.submission.feedback}"</p>
                                 <div className="mt-4 text-2xl font-black text-indigo-400">{activeTask.submission.score} / {activeTask.max_points}</div>
                              </motion.div>
                           )}
                        </div>

                        {/* Core Editor / Dropzone workspace */}
                        <div className="flex-1 bg-[#0d1117] relative flex flex-col overflow-hidden">
                           {activeTask.type === "coding" ? (
                              <>
                                 {/* Coding Top Bar */}
                                 <div className="h-12 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6 shrink-0">
                                    <div className="relative flex items-center">
                                       <select value={lang} onChange={e => setLang(e.target.value)} className="bg-slate-800 text-white text-xs font-bold px-3 py-1.5 pr-8 rounded-lg border-none appearance-none outline-none cursor-pointer">
                                          {DEFAULT_LANGS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                                       </select>
                                       <ChevronDown size={12} className="absolute right-3 opacity-50 pointer-events-none" />
                                    </div>

                                    <div className="flex gap-2">
                                       <button
                                          onClick={handleCodeRun}
                                          disabled={isRunning}
                                          className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-lg transition-all disabled:opacity-50"
                                       >
                                          {isRunning ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Play size={12} fill="white" />}
                                          Execute Local
                                       </button>
                                    </div>
                                 </div>

                                 {/* The Text Editor */}
                                 <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                                    <div className="flex-1 relative h-full">
                                       <div className="absolute left-0 top-0 bottom-0 w-10 bg-[#0d1117] border-r border-slate-800 text-[10px] font-mono text-slate-600 flex flex-col items-center pt-4 select-none opacity-40">
                                          {[...Array(200)].map((_, i) => <div key={i} className="h-[22px] leading-[22px]">{i + 1}</div>)}
                                       </div>
                                       <textarea
                                          value={code}
                                          onChange={e => setCode(e.target.value)}
                                          spellCheck={false}
                                          className="absolute inset-0 left-10 w-[calc(100%-40px)] h-full bg-[#0d1117] text-[#e6edf3] font-mono text-[14px] p-4 outline-none resize-none leading-[22px] caret-indigo-400 selection:bg-indigo-900/50"
                                          placeholder="# Type solution logic..."
                                       />
                                    </div>

                                    {/* Results/Console output sub-pane */}
                                    <div className="w-full lg:w-[300px] bg-slate-900 border-t lg:border-t-0 lg:border-l border-slate-800 flex flex-col shrink-0 overflow-y-auto custom-scrollbar">
                                       <div className="p-4 bg-slate-950 text-[10px] font-black uppercase tracking-wider flex items-center gap-2 text-slate-500"><TerminalSquare size={14} /> Console Diagnostics</div>

                                       <div className="p-6 flex-1 text-xs font-mono whitespace-pre-wrap">
                                          {!runResults && !isRunning && <span className="text-slate-600">// Awaiting local execution payload</span>}
                                          {isRunning && <span className="text-indigo-400 animate-pulse">// Compiling environment components...</span>}
                                          {runResults && (
                                             <div className="space-y-4">
                                                {runResults.error ? (
                                                   <div className="text-red-400 font-bold">
                                                      <div className="uppercase opacity-50 text-[9px] mb-1">Error stream</div>
                                                      {runResults.error}
                                                   </div>
                                                ) : (
                                                   <div className="text-emerald-400">
                                                      <div className="text-slate-500 uppercase font-black text-[9px] mb-1 tracking-widest">Standard Output</div>
                                                      {runResults.output || "<No display outputs detected>"}
                                                   </div>
                                                )}
                                                <div className="pt-4 border-t border-slate-800 flex items-center gap-2 text-[10px] text-slate-600">
                                                   <Timer size={12} /> Simulated: {runResults.runtime}ms
                                                </div>
                                             </div>
                                          )}
                                       </div>
                                    </div>
                                 </div>
                              </>
                           ) : (
                              <div className="flex-1 flex items-center justify-center bg-slate-950/50">
                                 <motion.div
                                    initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                                    className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-3xl p-10 text-center relative overflow-hidden shadow-2xl"
                                 >
                                    <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-500" />

                                    <div className="p-6 bg-slate-950 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6 shadow-inner border border-slate-800 text-indigo-500">
                                       {uploading ? <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" /> : <CloudUpload size={40} />}
                                    </div>

                                    <h3 className="text-xl font-black text-white mb-2">{uploading ? "Uploading Vault Assets..." : "Drop Submission Payload"}</h3>
                                    <p className="text-slate-500 text-sm mb-8">Attach documentation, image archives, or final PDF output for review.</p>

                                    {activeTask.submission?.file_url && (
                                       <div className="mb-6 p-3 bg-emerald-950/30 border border-emerald-900/50 text-emerald-400 rounded-xl text-xs font-bold truncate flex items-center justify-center gap-2">
                                          <CheckCircle size={14} /> Existing file linked!
                                       </div>
                                    )}

                                    <label className={`w-full flex flex-col items-center justify-center py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-sm shadow-lg cursor-pointer transition-all shadow-indigo-500/20 ${uploading && 'opacity-50 pointer-events-none'}`}>
                                       <span>{activeTask.submission?.file_url ? "Replace Solution" : "Select Files to Push"}</span>
                                       <input type="file" className="hidden" onChange={(e) => { handleFileUpload(e); e.target.value = null; }} disabled={uploading} />
                                    </label>
                                 </motion.div>
                              </div>
                           )}
                        </div>
                     </main>
                  </div>
               )}
            </AnimatePresence>
         </main>
      </div>
   );
}
